import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { t } from "./i18n";

const ErpWritebackDialog = ({ open, onClose, ticketId }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [action, setAction] = useState("service_order");
  const [titulo, setTitulo] = useState("");
  const [texto, setTexto] = useState("");
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      setPreview(null);
      return;
    }
    if (!ticketId) return;
    let alive = true;
    setLoading(true);
    api.post("/erp-writeback/preview", { ticketId })
      .then(({ data }) => {
        if (!alive) return;
        setPreview(data);
        setAction(data.action === "note" ? "note" : "service_order");
        setTitulo(data.titulo || "");
        setTexto(data.texto || "");
      })
      .catch(err => { if (alive) { toastError(err); onCloseRef.current(); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, ticketId]);

  const support = preview?.support || {};
  const allowed = action === "note" ? support.note : support.serviceOrder;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/erp-writeback/run", { ticketId, action, texto, titulo, ruleId: preview?.rule?.id });
      toast.success(
        t("writebackOk")
          .replace("{{id}}", data.erpId || "")
          .replace(" {{protocol}}", data.erpProtocol ? ` (${data.erpProtocol})` : "")
      );
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("writebackTitle")}</DialogTitle>
      <DialogContent dividers>
        {loading && <Typography variant="body2"><CircularProgress size={14} /> {t("writebackLoading")}</Typography>}
        {!loading && preview && (
          <>
            <FormControl fullWidth size="small" variant="outlined" style={{ marginBottom: 12 }}>
              <InputLabel>{t("writebackAction")}</InputLabel>
              <Select label={t("writebackAction")} value={action} onChange={e => setAction(e.target.value)}>
                <MenuItem value="note" disabled={!support.note}>{t("writebackNote")}</MenuItem>
                <MenuItem value="service_order" disabled={!support.serviceOrder}>{t("writebackOs")}</MenuItem>
              </Select>
            </FormControl>
            {!allowed && <Typography variant="caption" color="error">{t("writebackUnsupported")} {support.notes || ""}</Typography>}
            <TextField fullWidth size="small" variant="outlined" label={t("writebackTitleField")} value={titulo} onChange={e => setTitulo(e.target.value)} style={{ marginBottom: 12 }} />
            <TextField fullWidth size="small" variant="outlined" multiline minRows={8} label={t("writebackText")} value={texto} onChange={e => setTexto(e.target.value)} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">{t("writebackCancel")}</Button>
        <Button onClick={send} color="primary" variant="contained" disabled={loading || sending || !allowed || !texto.trim()}>
          {sending ? <CircularProgress size={14} color="inherit" /> : t("writebackSend")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErpWritebackDialog;
